import { DEG } from '@physlab/core';
import { createQuestion, fmt, selectTemplates } from '../questionUtils.js';

export function buildInclineQuestions({ params, rng, difficultyLevel, exerciseCount, thinkingCount }) {
  const g = params.gravity;
  const thetaDeg = params.angle;
  const theta = thetaDeg * DEG;
  let mu = params.friction;
  if (Math.tan(theta) <= mu + 0.001) mu = Number((Math.tan(theta) * 0.55).toFixed(2));
  if (mu < 0.02) mu = 0.02;
  const mass = params.mass;
  const L = params.length;
  const v0 = params.initialSpeed;
  const a = Math.max(0.01, g * (Math.sin(theta) - mu * Math.cos(theta)));
  const vBottom = Math.sqrt(v0 * v0 + 2 * a * L);
  const time = (-v0 + Math.sqrt(v0 * v0 + 2 * a * L)) / a;
  const normal = mass * g * Math.cos(theta);
  const friction = mu * normal;
  const frictionWork = friction * L;
  const initialEnergy = 0.5 * mass * v0 * v0 + mass * g * L * Math.sin(theta);
  const finalEnergy = 0.5 * mass * vBottom * vBottom;
  const measuredA = Number((0.75 * a).toFixed(2));
  const muFromA = (g * Math.sin(theta) - measuredA) / (g * Math.cos(theta));
  const currentStatic = Math.tan(theta) <= params.friction;

  const exercises = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '斜面上的加速度',
          prompt: `质量 $m=${fmt(mass, 1)}\\ \\mathrm{kg}$ 的滑块从倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$ 的斜面顶端由静止释放，滑块与斜面间的动摩擦因数 $\\mu=${fmt(mu, 2)}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。求滑块下滑的加速度 $a$。`,
          formula: 'a=g(\\sin\\theta-\\mu\\cos\\theta)',
          answer: `$a\\approx ${fmt(a, 2)}\\ \\mathrm{m/s^2}$，方向沿斜面向下`,
          answerValue: a,
          unit: 'm/s²',
          solution: [
            '垂直斜面方向：$N=mg\\cos\\theta$，所以滑动摩擦力 $f=\\mu mg\\cos\\theta$。',
            '沿斜面向下为正：$mg\\sin\\theta-\\mu mg\\cos\\theta=ma$，质量约去得 $a=g(\\sin\\theta-\\mu\\cos\\theta)$。',
            `代入数据：$a=${fmt(g, 1)}\\times(\\sin ${fmt(thetaDeg, 0)}^\\circ-${fmt(mu, 2)}\\cos ${fmt(thetaDeg, 0)}^\\circ)\\approx ${fmt(a, 3)}\\ \\mathrm{m/s^2}$。`,
          ],
          knowledge: ['受力分析', '牛顿第二定律', '滑动摩擦力'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '底端速度',
          prompt: `倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$、长 $L=${fmt(L, 1)}\\ \\mathrm{m}$ 的斜面，动摩擦因数 $\\mu=${fmt(mu, 2)}$。滑块以 $v_0=${fmt(v0, 1)}\\ \\mathrm{m/s}$ 沿斜面向下运动，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。求滑块到达底端时的速度 $v$。`,
          formula: 'v^2=v_0^2+2aL',
          answer: `$v\\approx ${fmt(vBottom, 2)}\\ \\mathrm{m/s}$`,
          answerValue: vBottom,
          unit: 'm/s',
          solution: [
            `先求加速度：$a=g(\\sin\\theta-\\mu\\cos\\theta)\\approx ${fmt(a, 2)}\\ \\mathrm{m/s^2}$。`,
            `滑块做匀加速直线运动，$v^2=v_0^2+2aL$。`,
            `代入：$v=\\sqrt{${fmt(v0, 1)}^2+2\\times${fmt(a, 2)}\\times${fmt(L, 1)}}\\approx ${fmt(vBottom, 2)}\\ \\mathrm{m/s}$。`,
          ],
          knowledge: ['匀变速直线运动', '斜面加速度'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '下滑时间',
          prompt: `倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$、长 $L=${fmt(L, 1)}\\ \\mathrm{m}$ 的斜面，滑块以 $v_0=${fmt(v0, 1)}\\ \\mathrm{m/s}$ 的初速度沿斜面向下运动，加速度 $a=${fmt(a, 2)}\\ \\mathrm{m/s^2}$。求滑块到达底端所用的时间 $t$（保留两位小数）。`,
          formula: 'L=v_0t+\\tfrac12at^2',
          answer: `$t\\approx ${fmt(time, 2)}\\ \\mathrm{s}$`,
          answerValue: time,
          unit: 's',
          solution: [
            `由位移公式：$${fmt(L, 1)}=${fmt(v0, 1)}t+\\tfrac12\\times${fmt(a, 2)}t^2$。`,
            `解一元二次方程，取正根：$t=\\dfrac{-v_0+\\sqrt{v_0^2+2aL}}{a}$。`,
            `$t=\\dfrac{-${fmt(v0, 1)}+\\sqrt{${fmt(v0, 1)}^2+2\\times${fmt(a, 2)}\\times${fmt(L, 1)}}}{${fmt(a, 2)}}\\approx ${fmt(time, 3)}\\ \\mathrm{s}$。`,
          ],
          knowledge: ['匀变速直线运动', '一元二次方程'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '摩擦生热',
          prompt: `质量 $m=${fmt(mass, 1)}\\ \\mathrm{kg}$ 的滑块沿倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$、长 $L=${fmt(L, 1)}\\ \\mathrm{m}$ 的斜面下滑，动摩擦因数 $\\mu=${fmt(mu, 2)}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。求整个下滑过程中摩擦产生的热量 $Q$。`,
          formula: 'Q=\\mu mgL\\cos\\theta',
          answer: `$Q\\approx ${fmt(frictionWork, 2)}\\ \\mathrm{J}$`,
          answerValue: frictionWork,
          unit: 'J',
          solution: [
            `支持力 $N=mg\\cos\\theta=${fmt(mass, 1)}\\times${fmt(g, 1)}\\times\\cos ${fmt(thetaDeg, 0)}^\\circ\\approx ${fmt(normal, 2)}\\ \\mathrm{N}$。`,
            `摩擦力 $f=\\mu N\\approx ${fmt(friction, 2)}\\ \\mathrm{N}$。`,
            `摩擦生热等于克服摩擦力做功：$Q=fL\\approx ${fmt(friction, 2)}\\times${fmt(L, 1)}\\approx ${fmt(frictionWork, 2)}\\ \\mathrm{J}$。`,
          ],
          knowledge: ['摩擦生热', '功与能', '支持力'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 3,
          topic: '由加速度反求动摩擦因数',
          prompt: `在倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$ 的斜面上，测得滑块下滑的加速度为 $a=${fmt(measuredA, 2)}\\ \\mathrm{m/s^2}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。求滑块与斜面间的动摩擦因数 $\\mu$（保留两位小数）。`,
          formula: '\\mu=\\dfrac{g\\sin\\theta-a}{g\\cos\\theta}',
          answer: `$\\mu\\approx ${fmt(muFromA, 2)}$`,
          answerValue: muFromA,
          solution: [
            '由 $a=g(\\sin\\theta-\\mu\\cos\\theta)$ 解出 $\\mu$。',
            `$\\mu=\\dfrac{${fmt(g, 1)}\\sin ${fmt(thetaDeg, 0)}^\\circ-${fmt(measuredA, 2)}}{${fmt(g, 1)}\\cos ${fmt(thetaDeg, 0)}^\\circ}$。`,
            `计算得 $\\mu\\approx ${fmt(muFromA, 3)}$。检验：$\\tan\\theta\\approx ${fmt(Math.tan(theta), 2)}$，若 $\\mu<\\tan\\theta$，滑块确实能加速下滑。`,
          ],
          knowledge: ['动摩擦因数测量', '牛顿第二定律'],
        }),
    },
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `incline-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '斜面上的静摩擦平衡',
          prompt: `滑块放在倾角 $\\theta=${fmt(thetaDeg, 0)}^\\circ$ 的斜面上，接触面间动摩擦因数（近似取最大静摩擦因数）$\\mu=${fmt(params.friction, 2)}$。请通过比较 $\\tan\\theta$ 与 $\\mu$ 判断滑块能否保持静止。`,
          formula: '\\tan\\theta\\le\\mu',
          answer: currentStatic
            ? `$\\tan\\theta=${fmt(Math.tan(theta), 2)}\\le\\mu=${fmt(params.friction, 2)}$，可以保持静止。`
            : `$\\tan\\theta=${fmt(Math.tan(theta), 2)}>\\mu=${fmt(params.friction, 2)}$，不能保持静止，将沿斜面下滑。`,
          solution: [
            `重力沿斜面的分量为 $mg\\sin\\theta$，最大静摩擦力约为 $\\mu mg\\cos\\theta$。`,
            `静止条件为 $mg\\sin\\theta\\le\\mu mg\\cos\\theta$，即 $\\tan\\theta\\le\\mu$。`,
            `本例 $\\tan\\theta=${fmt(Math.tan(theta), 3)}$，$\\mu=${fmt(params.friction, 2)}$，所以${currentStatic ? '可以' : '不能'}保持静止。`,
          ],
          knowledge: ['静摩擦力', '临界条件', '受力分析'],
        }),
    },
  ];

  const thinking = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `incline-th-${index}`,
          type: 'thinking',
          level: 1,
          topic: '质量对加速度的影响',
          prompt: '同一斜面上，质量不同的两个滑块（动摩擦因数相同）下滑的加速度是否相同？为什么？',
          formula: 'a=g(\\sin\\theta-\\mu\\cos\\theta)',
          answer:
            '相同。重力沿斜面的分量和摩擦力都与质量成正比，质量在牛顿第二定律中约去，因此加速度与质量无关。',
          solution: [
            '沿斜面方向：$mg\\sin\\theta-\\mu mg\\cos\\theta=ma$。',
            '等式两边同时约去 $m$，得到 $a=g(\\sin\\theta-\\mu\\cos\\theta)$。',
            '所以同一斜面上、动摩擦因数相同时，不同质量的滑块加速度相同（忽略空气阻力）。',
          ],
          knowledge: ['牛顿第二定律', '质量与加速度'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `incline-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '机械能转化',
          prompt: '滑块从斜面上由静止下滑，若斜面粗糙，它的机械能是否守恒？减少的机械能转化成了什么？如何用实验检验？',
          formula: '\\Delta E_{\\text{机}}=-W_f',
          answer:
            '不守恒。减少的机械能等于克服摩擦力做的功，转化为滑块和斜面的内能。可测量摩擦生热并与机械能减少量比较。',
          solution: [
            '重力做正功，使重力势能转化为动能；摩擦力做负功，使部分机械能转化为内能。',
            '能量关系：$mgL\\sin\\theta=\\tfrac12mv^2+Q$，其中 $Q=\\mu mgL\\cos\\theta$。',
            '实验检验：测出初末高度与速度算机械能减少量，再测摩擦力与位移算 $Q$，比较两者是否相等。',
          ],
          knowledge: ['机械能守恒条件', '摩擦生热', '功能关系'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `incline-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '倾角对运动的影响',
          prompt: '斜面长度和动摩擦因数不变，仅增大倾角，滑块下滑的加速度、到达底端的时间和速度分别怎样变化？',
          formula: 'a=g(\\sin\\theta-\\mu\\cos\\theta)',
          answer:
            '加速度增大，到达底端的时间缩短，底端速度增大。因为重力沿斜面的分量增大，而摩擦力减小。',
          solution: [
            '由 $a=g(\\sin\\theta-\\mu\\cos\\theta)$，$\\theta$ 增大时 $\\sin\\theta$ 增大、$\\cos\\theta$ 减小，所以 $a$ 增大。',
            '位移 $L$ 不变，初速度相同的情况下，加速度越大，运动时间越短（$L=v_0t+\\tfrac12at^2$）。',
            '由 $v^2=v_0^2+2aL$，加速度增大，底端速度也增大。',
          ],
          knowledge: ['斜面加速度', '运动学关系', '控制变量'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `incline-th-${index}`,
          type: 'thinking',
          level: 3,
          topic: '测量动摩擦因数的方案',
          prompt: '请设计一个利用斜面测量滑块与斜面间动摩擦因数的实验方案，说明需要测量哪些量、如何处理数据，以及如何减小误差。',
          formula: '\\mu=\\dfrac{g\\sin\\theta-a}{g\\cos\\theta}',
          answer:
            '固定倾角，测位移 L 与下滑时间 t，由 L=½at² 求 a，再由 μ=(g sinθ−a)/(g cosθ) 求 μ。多次测量取平均，并改变倾角作 a–sinθ 图像提高精度。',
          solution: [
            '器材：可调倾角斜面、滑块、刻度尺、计时器（或光电门）、量角器。',
            '由静止释放，测位移 $L$ 和对应时间 $t$，用 $a=\\dfrac{2L}{t^2}$ 求加速度。',
            '由牛顿第二定律 $a=g(\\sin\\theta-\\mu\\cos\\theta)$ 反解 $\\mu$。',
            '减小误差：多次测量取平均；用光电门代替秒表；改变倾角获得多组数据，作 $a$–$\\sin\\theta$ 图像，由斜率和截距求 $\\mu$；确保斜面清洁且滑块不左右偏转。',
          ],
          knowledge: ['实验设计', '误差分析', '图像法'],
        }),
    },
  ];

  const selectedExercises = selectTemplates(rng, exercises, exerciseCount, difficultyLevel);
  const selectedThinking = selectTemplates(rng, thinking, thinkingCount, difficultyLevel);
  return {
    exercises: selectedExercises.map((template, index) => template.build(index + 1)),
    thinking: selectedThinking.map((template, index) => template.build(index + 1)),
  };
}
